import type { BuildConfig } from './build.types';
import type {
  CheckoutAmounts,
  CheckoutInstance,
  CheckoutState,
  ErrorCode,
  ReadyEvent,
} from './core.types';
import type { EkkoApp, MountEmit, MountHandle, MountHost } from './contract.types';
import type { ResolvedOptions } from './options';
import { createEmitter } from './emitter';
import { preloadTranslations } from './hints';
import { mintQuote } from './quote';
import { createWidget, type WidgetElement } from './skeleton';

// From initCheckout: the app must have arrived within APP_LOAD_MS, and the
// widget (or its error) must be painted within WATCHDOG_MS, half a second past
// the quote's own deadline, so a shopper never watches the skeleton for longer
// than that (decided 2026-10-21). Either one rests the widget on the ekko card.
export const APP_LOAD_MS = 5_000;
export const WATCHDOG_MS = 8_000;

/**
 * skeleton -> mounted -> live, or failed, or destroyed. `failed` and
 * `destroyed` are terminal.
 */
export type MountState = 'skeleton' | 'mounted' | 'live' | 'failed' | 'destroyed';

export interface Instance {
  api: CheckoutInstance;
  state(): MountState;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

const empty = (): CheckoutState => ({
  quoteId: null,
  impactType: null,
  currencyCode: null,
  totalAmount: 0,
  breakdown: { credits: 0, roundUp: 0, suggestedAmounts: 0 },
  allocation: { credits: null, contribution: null },
});

export function createInstance(
  options: ResolvedOptions,
  config: BuildConfig,
  app: Promise<EkkoApp>,
  loadScript: MountHost['loadScript'],
): Instance {
  const emitter = createEmitter();
  for (const [name, handler] of Object.entries(options.on)) emitter.on(name, handler);

  const { sessionId, clientSecret, sessionData, locale, appearance, features } = options.mount;
  // First, so a widget that cannot be drawn leaves no request or timer behind.
  // A browser without constructable stylesheets throws here: fail, never throw.
  let widget: WidgetElement;
  try {
    const rewardBar = (features as { rewardBar?: unknown } | undefined)?.rewardBar !== false;
    widget = createWidget(options.container, appearance, rewardBar);
  } catch (error) {
    queueMicrotask(() =>
      emitter.emit('error', { code: 'RENDER_ERROR', message: `[ekko-checkout] ${messageOf(error)}` }),
    );
    return { state: () => 'failed', api: { on: emitter.on, getState: empty, destroy: emitter.clear } };
  }
  const mint = mintQuote(config.api, sessionId, clientSecret, sessionData);
  if (locale) preloadTranslations(config, locale);
  const controller = new AbortController();

  let state: MountState = 'skeleton';
  let handle: MountHandle | undefined;
  let quoteOk = false;
  let pendingReady: ReadyEvent | undefined;
  let pendingAmounts: CheckoutAmounts | undefined;
  let readySent = false;
  let errorSent = false;
  let snapshot = empty();

  const watchdog = setTimeout(
    () => fail('APP_TIMEOUT', `The checkout did not render within ${WATCHDOG_MS / 1000} seconds.`),
    WATCHDOG_MS,
  );
  const appTimer = setTimeout(
    () => fail('APP_TIMEOUT', `The checkout app did not load within ${APP_LOAD_MS / 1000} seconds.`),
    APP_LOAD_MS,
  );

  mint.result.then((outcome) => {
    quoteOk = outcome.state === 'ok';
    flushReady();
  });
  app.then(mount, (error) => fail('APP_LOAD_FAILED', messageOf(error)));

  const active = () => state === 'mounted' || state === 'live';

  const emit: MountEmit = {
    painted(kind) {
      // The app's error state is never shown: the widget rests on the ekko
      // card, as for any other failure. A live widget that fails ends here,
      // so getState() hands out no quote the shopper can no longer see; the
      // app has already sent its error. Before that (a failed quote), the
      // app's error may follow.
      if (kind === 'error') {
        if (state === 'live') fail('RENDER_ERROR', 'The widget failed.');
        else if (state === 'mounted') stop();
        return;
      }
      // 'widget' goes live. 'empty' (the app drew nothing on purpose) reveals
      // the empty slot and ends there: no card, no event.
      if (state !== 'mounted') return;
      widget.reveal();
      if (kind === 'empty') return end('failed');
      state = 'live';
      clearTimeout(watchdog);
      flushReady();
    },
    ready(payload, amounts) {
      pendingReady = payload;
      pendingAmounts = amounts;
      flushReady();
    },
    toggleChanged(payload, amounts) {
      if (!active()) return;
      record(amounts, payload.quoteId, payload.impactType);
      emitter.emit('toggleChanged', payload);
    },
    error(payload) {
      if (state === 'destroyed' || (state === 'failed' && errorSent)) return;
      errorSent = true;
      emitter.emit('error', payload);
    },
  };

  function mount(loaded: EkkoApp): void {
    if (state !== 'skeleton') return;
    clearTimeout(appTimer);
    state = 'mounted';
    const host: MountHost = {
      root: widget.root,
      slot: widget.slot,
      options: options.mount,
      api: config.api,
      content: config.content,
      quote: mint.result,
      signal: controller.signal,
      emit,
      loadScript,
    };
    if (config.analytics) host.analytics = config.analytics;
    try {
      handle = loaded.mount(host);
      // A merchant can destroy() from an error the app emits inside mount, and
      // the app can paint its error there, before there was a handle to unmount.
      if (!active()) unmount();
    } catch (error) {
      fail('RENDER_ERROR', messageOf(error));
    }
  }

  function flushReady(): void {
    if (state !== 'live' || !quoteOk || !pendingReady || readySent) return;
    readySent = true;
    // Applied once, as ready goes out, and never over amounts a toggle already set.
    record(snapshot.currencyCode ? undefined : pendingAmounts, pendingReady.quoteId, pendingReady.impactType);
    emitter.emit('ready', pendingReady);
  }

  // The app works the amounts out; core only keeps the latest for getState().
  function record(amounts?: CheckoutAmounts, quoteId?: string, impactType?: CheckoutState['impactType']): void {
    snapshot = { ...snapshot, ...amounts };
    if (quoteId) snapshot.quoteId = quoteId;
    if (impactType) snapshot.impactType = impactType;
  }

  function end(next: 'failed' | 'destroyed'): void {
    state = next;
    clearTimeout(watchdog);
    clearTimeout(appTimer);
    mint.cancel();
    controller.abort();
  }

  function unmount(): void {
    const current = handle;
    handle = undefined;
    try {
      current?.unmount();
    } catch (error) {
      console.error(error);
    }
  }

  /** Ends the widget as failed and settles it into the ekko card. */
  function stop(): void {
    end('failed');
    // Measured before the unmount empties the slot.
    widget.rest();
    unmount();
  }

  function fail(code: ErrorCode, message: string): void {
    if (state === 'failed' || state === 'destroyed') return;
    stop();
    // The app's own error already told the merchant why.
    if (errorSent) return;
    errorSent = true;
    emitter.emit('error', { code, message: `[ekko-checkout] ${message}` });
  }

  return {
    state: () => state,
    api: {
      on(event, handler) {
        if (state === 'destroyed') return () => undefined;
        return emitter.on(event, handler);
      },
      getState() {
        return state === 'failed' ? empty() : structuredClone(snapshot);
      },
      destroy() {
        if (state === 'destroyed') return;
        end('destroyed');
        unmount();
        widget.element.remove();
        emitter.clear();
      },
    },
  };
}
